import React, {useEffect, useMemo, useState} from 'react';
import {useQuery} from '@tanstack/react-query';
import {Button, Form, Modal, Spinner} from 'react-bootstrap';
import {IconChartBar, IconChartLine, IconChartPie, IconNumber, IconTable} from '@tabler/icons-react';
import {apiService} from '../services/api';
import {initialStatisticFilterValues} from '../utils/statisticValues';
import {StatisticChartDef, StatisticFilterDef, StatisticWidgetDef} from '../types';
import {StatisticWidgetView} from './StatisticWidgetView';
import {WidgetSkeleton} from './WidgetSkeleton';
import './StatisticWidgetEditorModal.css';

type WidgetType = StatisticWidgetDef['type'];
type Draft = Pick<StatisticWidgetDef, 'displayName' | 'type' | 'x' | 'y' | 'columns' | 'limit'>;

export const WIDGET_TYPES: {value: WidgetType; label: string; hint: string; icon: React.ReactNode}[] = [
    {value: 'table', label: 'Tabuľka', hint: 'Riadky štatistiky', icon: <IconTable size={22}/>},
    {value: 'kpi', label: 'Číslo', hint: 'Jedna hodnota', icon: <IconNumber size={22}/>},
    {value: 'bar', label: 'Stĺpcový', hint: 'Porovnanie', icon: <IconChartBar size={22}/>},
    {value: 'line', label: 'Čiarový', hint: 'Vývoj v čase', icon: <IconChartLine size={22}/>},
    {value: 'pie', label: 'Koláčový', hint: 'Podiely', icon: <IconChartPie size={22}/>},
];

/** The builder definition as the API returns it (only what the editor reads). */
export interface WidgetEditorDefinition {
    id: string;
    displayName: string;
    filters: {code: string; displayName: string; type: StatisticFilterDef['type']; defaultValue: string | null; required: boolean; targetScreen: string | null}[];
    widgets: StatisticWidgetDef[];
    suggestedWidget: StatisticChartDef | null;
    columns: string[];
    [key: string]: any;
}

const unwrap = (response: any): {ok: boolean; data?: any; message?: string} => {
    if (!response.success) return {ok: false, message: response.message || 'Nastala chyba.'};
    const body = response.data;
    if (body?.success === false) return {ok: false, message: body.message || 'Nastala chyba.'};
    return {ok: true, data: body?.data ?? body};
};

const toggle = (list: string[], column: string, single: boolean) =>
    list.includes(column) ? list.filter(c => c !== column) : single ? [column] : [...list, column];

/**
 * Create / edit / delete one widget of a statistic in a modal, with a live
 * preview over the statistic's real result (client admin). `widgetId` null =
 * new widget, prefilled with the assistant's suggestion. `onSaved` gets the
 * updated statistic definition (also after a delete).
 */
export const StatisticWidgetEditorModal: React.FC<{
    show: boolean;
    statisticId: string;
    widgetId: string | null;
    onHide: () => void;
    onSaved: (definition: WidgetEditorDefinition) => void;
}> = ({show, statisticId, widgetId, onHide, onSaved}) => {
    const isNew = widgetId === null;
    const [definition, setDefinition] = useState<WidgetEditorDefinition | null>(null);
    const [draft, setDraft] = useState<Draft | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);

    useEffect(() => {
        if (!show) return;
        setError(null);
        setConfirmDelete(false);
        setDraft(null);

        const start = (loaded: WidgetEditorDefinition) => {
            setDefinition(loaded);
            const existing = loaded.widgets.find(widget => widget.id === widgetId);
            if (!isNew && !existing) {
                setError('Widget neexistuje.');
                return;
            }
            setDraft(existing
                ? {displayName: existing.displayName ?? loaded.displayName, type: existing.type, x: existing.x, y: existing.y, columns: existing.columns, limit: existing.limit ?? null}
                : {
                    displayName: loaded.displayName,
                    type: loaded.suggestedWidget?.type ?? 'table',
                    x: loaded.suggestedWidget?.x ?? null,
                    y: loaded.suggestedWidget?.y ?? [],
                    columns: [],
                    limit: null,
                });
        };

        let cancelled = false;
        apiService.getStatisticDefinition(statisticId).then(response => {
            if (cancelled) return;
            const u = unwrap(response);
            if (u.ok) {
                start(u.data);
            } else {
                setError(u.message || null);
            }
        });
        return () => { cancelled = true; };
    }, [show, statisticId, widgetId, isNew]);

    const filterDefs: StatisticFilterDef[] = useMemo(() => (definition?.filters ?? []).map(filter => ({
        code: filter.code,
        displayName: filter.displayName,
        type: filter.type,
        required: filter.required,
        defaultValue: filter.defaultValue,
        targetScreenId: filter.targetScreen,
    })), [definition]);
    const values = useMemo(() => initialStatisticFilterValues(filterDefs), [filterDefs]);
    const missing = filterDefs.filter(filter => filter.required && !values[filter.code]);

    const {data, isLoading} = useQuery({
        queryKey: ['statisticData', statisticId, values],
        queryFn: ({signal}) => apiService.getStatisticData(statisticId, values, signal),
        enabled: show && !!definition && missing.length === 0,
    });

    const columns = data?.columns?.length ? data.columns : (definition?.columns ?? []);
    const options = draft ? Array.from(new Set([...columns, ...(draft.x ? [draft.x] : []), ...draft.y, ...draft.columns])) : [];
    const update = (patch: Partial<Draft>) => setDraft(current => (current ? {...current, ...patch} : current));
    const single = draft?.type === 'kpi' || draft?.type === 'pie';

    const save = async () => {
        if (!draft) return;
        setSaving(true);
        setError(null);
        const typedTitle = draft.displayName?.trim() ?? '';
        const payload = {
            ...draft,
            // the statistic's own name is the default: stored as null so the widget follows a rename
            displayName: typedTitle === '' || typedTitle === definition?.displayName ? null : typedTitle,
            x: draft.type === 'table' || draft.type === 'kpi' ? null : draft.x,
            limit: draft.type === 'kpi' ? null : draft.limit ?? null,
        };
        const u = unwrap(isNew
            ? await apiService.createStatisticWidget(statisticId, payload)
            : await apiService.updateStatisticWidget(statisticId, widgetId as string, payload));
        setSaving(false);
        if (u.ok) {
            onSaved(u.data);
        } else {
            setError(u.message || null);
        }
    };

    const remove = async () => {
        if (isNew) return;
        setSaving(true);
        const u = unwrap(await apiService.deleteStatisticWidget(statisticId, widgetId as string));
        setSaving(false);
        if (u.ok) {
            onSaved(u.data);
        } else {
            setError(u.message || null);
            setConfirmDelete(false);
        }
    };

    const previewWidget: StatisticWidgetDef | null = draft ? {id: widgetId ?? 'new', sortOrder: 0, ...draft} : null;
    const title = draft?.displayName || definition?.displayName || '';

    return (
        <Modal show={show} onHide={onHide} size="xl" className="modal-blur statistic-widget-editor" centered>
            <Modal.Header closeButton>
                <Modal.Title>{isNew ? 'Nový widget' : 'Upraviť widget'}{definition ? <span className="text-muted fw-normal"> · {definition.displayName}</span> : null}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {error && <div className="alert alert-danger">{error}</div>}
                {!draft ? (
                    !error && <div className="text-center py-5"><Spinner/></div>
                ) : (
                    <div className="row g-3">
                        <div className="col-lg-5">
                            <Form.Label htmlFor="widget-title">Titulok <span className="text-muted small">(predvolene názov štatistiky)</span></Form.Label>
                            <Form.Control id="widget-title" className="mb-3" placeholder={definition?.displayName} value={draft.displayName ?? ''}
                                          onChange={e => update({displayName: e.target.value})}/>

                            <Form.Label>Typ</Form.Label>
                            <div className="widget-type-tiles mb-3" role="radiogroup" aria-label="Typ widgetu">
                                {WIDGET_TYPES.map(option => (
                                    <button key={option.value} type="button" role="radio" aria-checked={draft.type === option.value}
                                            className={`widget-type-tile${draft.type === option.value ? ' is-active' : ''}`}
                                            onClick={() => update({type: option.value, y: option.value === 'kpi' || option.value === 'pie' ? draft.y.slice(0, 1) : draft.y})}>
                                        {option.icon}
                                        <strong>{option.label}</strong>
                                        <span>{option.hint}</span>
                                    </button>
                                ))}
                            </div>

                            {options.length === 0 && (
                                <div className="text-muted small mb-2">Štatistika zatiaľ nevracia žiadne stĺpce.</div>
                            )}

                            {draft.type !== 'table' && draft.type !== 'kpi' && (
                                <div className="mb-3">
                                    <Form.Label>Os X <span className="text-muted small">(kategória alebo dátum)</span></Form.Label>
                                    <Form.Select value={draft.x ?? ''} onChange={e => update({x: e.target.value || null})}>
                                        <option value="">—</option>
                                        {options.map(column => <option key={column} value={column}>{column}</option>)}
                                    </Form.Select>
                                </div>
                            )}

                            {draft.type !== 'table' && (
                                <div className="mb-3">
                                    <Form.Label>{single ? 'Hodnota' : 'Os Y'} <span className="text-muted small">({single ? 'jeden číselný stĺpec' : 'číselné stĺpce'})</span></Form.Label>
                                    <div className="d-flex flex-wrap gap-3">
                                        {options.map(column => (
                                            <Form.Check key={column} type={single ? 'radio' : 'checkbox'} id={`widget-y-${column}`} label={column}
                                                        checked={draft.y.includes(column)} onChange={() => update({y: toggle(draft.y, column, single)})}/>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {draft.type === 'table' && (
                                <div className="mb-1">
                                    <Form.Label>Stĺpce <span className="text-muted small">(nič nevybraté = všetky)</span></Form.Label>
                                    <div className="d-flex flex-wrap gap-3">
                                        {options.map(column => (
                                            <Form.Check key={column} type="checkbox" id={`widget-col-${column}`} label={column}
                                                        checked={draft.columns.includes(column)} onChange={() => update({columns: toggle(draft.columns, column, false)})}/>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {draft.type !== 'kpi' && (
                                <div className="mt-3">
                                    <Form.Label htmlFor="widget-limit">
                                        {draft.type === 'pie' ? 'Počet výsekov' : 'Počet riadkov'}{' '}
                                        <span className="text-muted small">
                                            ({draft.type === 'pie' ? 'najväčšie hodnoty, zvyšok sa spojí do „Ostatné“' : 'prvé riadky výsledku'}; prázdne = všetky)
                                        </span>
                                    </Form.Label>
                                    <Form.Control id="widget-limit" type="number" min={1} step={1} inputMode="numeric" placeholder="Všetky"
                                                  style={{maxWidth: 160}} value={draft.limit ?? ''}
                                                  onChange={e => update({limit: e.target.value === '' ? null : Number(e.target.value)})}/>
                                </div>
                            )}
                        </div>

                        <div className="col-lg-7">
                            <Form.Label>Náhľad</Form.Label>
                            <div className="widget-preview">
                                <div className="widget-preview-title">{title}</div>
                                {missing.length > 0 ? (
                                    <p className="text-muted small mb-0">
                                        Náhľad potrebuje hodnotu filtra {missing.map(filter => `„${filter.displayName}“`).join(', ')} — nastavte predvolenú hodnotu v štatistike.
                                    </p>
                                ) : isLoading || !previewWidget ? (
                                    <div style={{height: draft.type === 'kpi' ? 120 : 300}}><WidgetSkeleton type={draft.type}/></div>
                                ) : (
                                    <StatisticWidgetView widget={previewWidget} columns={data?.columns ?? []} rows={data?.rows ?? []}
                                                         height={draft.type === 'kpi' ? 120 : 300}/>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer className="justify-content-between">
                <div>
                    {!isNew && draft && (confirmDelete ? (
                        <span className="d-inline-flex align-items-center gap-2">
                            <span className="small text-danger">Zmazať widget? Zmizne aj z domovských stránok používateľov.</span>
                            <Button size="sm" variant="danger" onClick={remove} disabled={saving}>Zmazať</Button>
                            <Button size="sm" variant="outline-secondary" onClick={() => setConfirmDelete(false)}>Nie</Button>
                        </span>
                    ) : (
                        <Button variant="outline-danger" onClick={() => setConfirmDelete(true)}>Zmazať widget</Button>
                    ))}
                </div>
                <div className="d-flex gap-2">
                    <Button variant="outline-secondary" onClick={onHide}>Zrušiť</Button>
                    <Button onClick={save} disabled={!draft || saving}>{saving ? <Spinner size="sm"/> : 'Uložiť'}</Button>
                </div>
            </Modal.Footer>
        </Modal>
    );
};
